import React, { useState, useEffect, useCallback } from 'react';
import { Upload, Trash2, Image as ImageIcon, Info, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminLogoSettings = () => {
  const [logoSettings, setLogoSettings] = useState(null);
  const [guidelines, setGuidelines] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadType, setUploadType] = useState(null);

  const fetchSettings = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/site-settings`, {
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setLogoSettings(data);
      }
    } catch (err) {
      console.error('Failed to fetch logo settings:', err);
    }
  }, []);

  const fetchGuidelines = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/site-settings/logo-guidelines`, {
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setGuidelines(data);
      }
    } catch (err) {
      console.error('Failed to fetch guidelines:', err);
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchSettings(), fetchGuidelines()]);
      setLoading(false);
    };
    loadData();
  }, [fetchSettings, fetchGuidelines]);

  const handleUpload = async (e, logoType) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/svg+xml', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload PNG, JPG, SVG, or WebP.');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 5MB.');
      return;
    }

    setUploading(true);
    setUploadType(logoType);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('logo_type', logoType);

      const res = await fetch(`${API_URL}/api/site-settings/admin/logo?logo_type=${logoType}`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (res.ok) {
        toast.success(`${logoType === 'main' ? 'Logo' : 'Favicon'} uploaded successfully!`);
        await fetchSettings();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to upload logo');
      }
    } catch (err) {
      console.error('Upload error:', err);
      toast.error('Failed to upload logo');
    } finally {
      setUploading(false);
      setUploadType(null);
    }
  };

  const handleDelete = async (logoType) => {
    if (!window.confirm(`Are you sure you want to delete the ${logoType === 'main' ? 'logo' : 'favicon'}?`)) {
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/site-settings/admin/logo?logo_type=${logoType}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (res.ok) {
        toast.success(`${logoType === 'main' ? 'Logo' : 'Favicon'} deleted successfully!`);
        await fetchSettings();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to delete');
      }
    } catch (err) {
      console.error('Delete error:', err);
      toast.error('Failed to delete');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-gold-600" />
      </div>
    );
  }

  const currentLogo = logoSettings?.logo?.logo_url;
  const currentFavicon = logoSettings?.logo?.favicon_url;

  return (
    <div className="space-y-8" data-testid="admin-logo-settings">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-heading font-semibold text-ivory-800">Logo Settings</h1>
        <p className="text-ivory-500 font-body mt-1">
          Upload and manage your company logo. The logo will appear in the navigation bar, footer, and other places across the site.
        </p>
      </div>

      {/* Guidelines Card */}
      {guidelines && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="font-semibold text-blue-900 mb-2">Recommended Logo Specifications</h3>
              <div className="grid md:grid-cols-2 gap-6 text-sm">
                <div>
                  <h4 className="font-medium text-blue-800 mb-1">Main Logo</h4>
                  <ul className="text-blue-700 space-y-1">
                    <li>• Size: <strong>400×100px</strong> (horizontal) or <strong>200×200px</strong> (square)</li>
                    <li>• Format: PNG (preferred), SVG, WebP, or JPG</li>
                    <li>• Use transparent background for best results</li>
                    <li>• Max file size: 5MB</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-medium text-blue-800 mb-1">Favicon</h4>
                  <ul className="text-blue-700 space-y-1">
                    <li>• Size: <strong>32×32px</strong> or <strong>64×64px</strong></li>
                    <li>• Format: PNG, ICO, or SVG</li>
                    <li>• Keep it simple for small display</li>
                    <li>• Max file size: 1MB</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Logo Section */}
      <div className="bg-ivory-100 rounded-xl p-6 border border-ivory-300/50">
        <h2 className="text-lg font-heading font-semibold text-ivory-800 mb-4 flex items-center gap-2">
          <ImageIcon className="w-5 h-5 text-gold-600" />
          Main Logo
        </h2>
        
        <div className="flex flex-col md:flex-row gap-6">
          {/* Preview */}
          <div className="flex-shrink-0">
            <div className="w-64 h-32 bg-white rounded-lg border-2 border-dashed border-ivory-300 flex items-center justify-center overflow-hidden">
              {currentLogo ? (
                <img 
                  src={`${API_URL}${currentLogo}`}
                  alt="Current Logo"
                  className="max-w-full max-h-full object-contain p-2"
                  data-testid="current-logo-preview"
                />
              ) : (
                <div className="text-center text-ivory-400">
                  <ImageIcon className="w-10 h-10 mx-auto mb-2" />
                  <span className="text-sm">No logo uploaded</span>
                </div>
              )}
            </div>
            {logoSettings?.logo?.logo_uploaded_at && (
              <p className="text-xs text-ivory-500 mt-2">
                Uploaded: {new Date(logoSettings.logo.logo_uploaded_at).toLocaleDateString()}
              </p>
            )}
          </div>

          {/* Actions */}
          <div className="flex-1">
            <div className="flex flex-wrap gap-3">
              <label className="cursor-pointer">
                <input
                  type="file"
                  accept=".png,.jpg,.jpeg,.svg,.webp"
                  className="hidden"
                  onChange={(e) => handleUpload(e, 'main')}
                  disabled={uploading}
                  data-testid="logo-upload-input"
                />
                <Button
                  variant="outline"
                  className="pointer-events-none"
                  disabled={uploading && uploadType === 'main'}
                >
                  {uploading && uploadType === 'main' ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Upload className="w-4 h-4 mr-2" />
                  )}
                  {currentLogo ? 'Replace Logo' : 'Upload Logo'}
                </Button>
              </label>
              
              {currentLogo && (
                <Button
                  variant="outline"
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  onClick={() => handleDelete('main')}
                  data-testid="delete-logo-btn"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Logo
                </Button>
              )}
            </div>
            
            <p className="text-sm text-ivory-500 mt-3">
              The main logo appears in the navigation bar, footer, admin panel, and email templates.
            </p>
          </div>
        </div>
      </div>

      {/* Favicon Section */}
      <div className="bg-ivory-100 rounded-xl p-6 border border-ivory-300/50">
        <h2 className="text-lg font-heading font-semibold text-ivory-800 mb-4 flex items-center gap-2">
          <ImageIcon className="w-5 h-5 text-gold-600" />
          Favicon
        </h2>
        
        <div className="flex flex-col md:flex-row gap-6">
          {/* Preview */}
          <div className="flex-shrink-0">
            <div className="w-32 h-32 bg-white rounded-lg border-2 border-dashed border-ivory-300 flex items-center justify-center overflow-hidden">
              {currentFavicon ? (
                <img 
                  src={`${API_URL}${currentFavicon}`}
                  alt="Current Favicon"
                  className="w-16 h-16 object-contain"
                  data-testid="current-favicon-preview"
                />
              ) : (
                <div className="text-center text-ivory-400">
                  <ImageIcon className="w-8 h-8 mx-auto mb-1" />
                  <span className="text-xs">No favicon</span>
                </div>
              )}
            </div>
            {logoSettings?.logo?.favicon_uploaded_at && (
              <p className="text-xs text-ivory-500 mt-2">
                Uploaded: {new Date(logoSettings.logo.favicon_uploaded_at).toLocaleDateString()}
              </p>
            )}
          </div>

          {/* Actions */}
          <div className="flex-1">
            <div className="flex flex-wrap gap-3">
              <label className="cursor-pointer">
                <input
                  type="file"
                  accept=".png,.jpg,.jpeg,.svg,.webp,.ico"
                  className="hidden"
                  onChange={(e) => handleUpload(e, 'favicon')}
                  disabled={uploading}
                  data-testid="favicon-upload-input"
                />
                <Button
                  variant="outline"
                  className="pointer-events-none"
                  disabled={uploading && uploadType === 'favicon'}
                >
                  {uploading && uploadType === 'favicon' ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Upload className="w-4 h-4 mr-2" />
                  )}
                  {currentFavicon ? 'Replace Favicon' : 'Upload Favicon'}
                </Button>
              </label>
              
              {currentFavicon && (
                <Button
                  variant="outline"
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  onClick={() => handleDelete('favicon')}
                  data-testid="delete-favicon-btn"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Favicon
                </Button>
              )}
            </div>
            
            <p className="text-sm text-ivory-500 mt-3">
              The favicon appears in browser tabs and bookmarks. Keep it simple and recognizable.
            </p>
          </div>
        </div>
      </div>

      {/* Status */}
      <div className="bg-ivory-100 rounded-xl p-4 border border-ivory-300/50">
        <h3 className="font-medium text-ivory-800 mb-3">Current Status</h3>
        <div className="flex flex-wrap gap-4">
          <div className="flex items-center gap-2">
            {currentLogo ? (
              <CheckCircle className="w-4 h-4 text-green-600" />
            ) : (
              <AlertCircle className="w-4 h-4 text-amber-500" />
            )}
            <span className="text-sm text-ivory-600">
              Main Logo: {currentLogo ? 'Uploaded' : 'Using default'}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {currentFavicon ? (
              <CheckCircle className="w-4 h-4 text-green-600" />
            ) : (
              <AlertCircle className="w-4 h-4 text-amber-500" />
            )}
            <span className="text-sm text-ivory-600">
              Favicon: {currentFavicon ? 'Uploaded' : 'Using default'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLogoSettings;
